import React, { useState, useEffect } from "react";
import { Box, Button } from "@mui/material";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { tokens } from "../../utils";
import ThemeEditor from "../Theming/ThemeEditor";
import { RESOURCE_TYPES, getThemeConfig } from "../Theming/themeConfig";
import MatchGalleryToggle from "../Theming/MatchGalleryToggle";
import BusinessBoxThemePreview from "../Theming/BusinessBoxThemePreview";
import { getWheelBusinessBox } from "../../redux/selectors/segmentBuilderSelectors";
import { useUpdateBusinessBoxV2 } from "../../services/mutations";
import { selectProfile } from "../../redux/reducers/gallery";

// ThemesPopup's "Business Box" section. Reuses the EXISTING Business Box
// theme controls (Theming/ThemeEditor, resourceType="business_box" -- the
// SAME editor InnerForm's own "Theme" tab renders) and the SAME save
// mutation (useUpdateBusinessBoxV2) BusinessBoxDesign.jsx already uses --
// not rewritten, just re-hosted with its own live preview + the single
// Match-the-Gallery toggle (ADDED SPEC, Gordon 2026-09-17 10:40 PT).
const BusinessBoxThemeSection = () => {
  const wheelBbox = useSelector(getWheelBusinessBox);
  const profile = useSelector(selectProfile);
  const bbox = wheelBbox || {};
  const galleryTheme = profile?.gallery_theme;
  const [theme, setTheme] = useState(bbox.theme || {});
  const [saving, setSaving] = useState(false);
  const updateBox = useUpdateBusinessBoxV2();
  const config = getThemeConfig(RESOURCE_TYPES.BUSINESS_BOX);

  // Re-seed the draft only when the box's saved theme actually changes (e.g.
  // after a successful save re-fetches), never clobbering an in-progress edit.
  useEffect(() => {
    setTheme(bbox.theme || {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bbox.id]);

  const onSave = () => {
    if (!bbox.id) {
      toast.error("Create a Business Box first, then theme it.");
      return;
    }
    setSaving(true);
    updateBox.mutate(
      { id: bbox.id, theme },
      {
        onSuccess: () => {
          setSaving(false);
          toast.success("Business Box theme saved!");
        },
        onError: () => {
          setSaving(false);
          toast.error("Failed to save theme.");
        },
      }
    );
  };

  return (
    <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, gap: 3 }}>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <MatchGalleryToggle
          data-testid="businessbox-match-gallery"
          value={theme.match_gallery}
          tokens={config.matchTokens}
          onChange={(mg) => setTheme((t) => ({ ...t, match_gallery: mg }))}
        />
        <ThemeEditor
          resourceType={RESOURCE_TYPES.BUSINESS_BOX}
          value={theme}
          onChange={setTheme}
          hideMatchGallery
        />
        <Button
          data-testid="businessbox-theme-save"
          onClick={onSave}
          disabled={saving}
          sx={{
            mt: 1,
            background: tokens.brand.primaryActive,
            color: "#fff",
            textTransform: "none",
            px: 3,
            "&:hover": { background: "#7A18E6" },
          }}
        >
          {saving ? "Saving…" : "Save"}
        </Button>
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <BusinessBoxThemePreview theme={theme} galleryTheme={galleryTheme} />
      </Box>
    </Box>
  );
};

export default BusinessBoxThemeSection;
